<template>
  <section v-loading="loading" class="freight-editor">
    <div v-if="loadError" role="alert">
      <el-alert :title="loadError" type="error" :closable="false" /><el-button
        @click="load"
        >重新加载</el-button
      >
    </div>
    <template v-else-if="ready">
      <div class="editor-title">
        <div>
          <h2>{{ templateId ? "编辑运费模板" : "新建运费模板" }}</h2>
          <p>
            每个最小配送地区只归属一种规则；同一订单同一模板收取一次固定运费。
          </p>
        </div>
      </div>
      <el-alert
        v-if="form.warning"
        :title="form.warning"
        type="warning"
        :closable="false"
        show-icon
      />
      <div
        v-if="errors.length"
        ref="errors"
        class="freight-errors"
        tabindex="-1"
        role="alert"
      >
        <strong>暂时无法保存，请检查：</strong>
        <ul>
          <li v-for="error in errors" :key="error">{{ error }}</li>
        </ul>
      </div>
      <div class="editor-columns">
        <div class="editor-main">
          <el-card shadow="never"
            ><h3>基本信息</h3>
            <el-form label-width="100px" @submit.native.prevent>
              <el-form-item label="模板名称" required :error="nameError"
                ><el-input
                  v-model="form.name"
                  placeholder="请输入模板名称，最多30字"
                  maxlength="60"
                  label="模板名称"
                /><span class="field-help"
                  >{{ nameLength }} / 30 字（去除首尾空格）</span
                ></el-form-item
              >
            </el-form></el-card
          >
          <el-card
            v-for="target in targets"
            :key="target.key"
            shadow="never"
            :class="['rule-card', target.key]"
          >
            <div slot="header" class="rule-heading">
              <h3>{{ target.title }}</h3>
              <span>{{ target.regions.length }} 个配送地区</span>
            </div>
            <template v-if="target.key.startsWith('g')">
              <el-form label-width="100px"
                ><el-form-item
                  label="固定运费"
                  required
                  :error="feeError(target.index)"
                  ><el-input
                    v-model="form.groups[target.index].fee"
                    placeholder="0.00"
                    inputmode="decimal"
                    class="fee-input"
                    :label="`${target.title}固定运费`"
                    ><template slot="prepend">¥</template
                    ><template slot="append">元</template></el-input
                  ><span class="field-help"
                    >大于0，最多两位小数，最高999999.99元</span
                  ></el-form-item
                ></el-form
              >
            </template>
            <div class="region-tags">
              <el-tag
                v-for="tag in tags(target.regions)"
                :key="tag.key"
                closable
                :type="target.key === 'blocked' ? 'danger' : 'info'"
                @close="remove(target.key, tag.ids)"
                >{{ tag.label }}</el-tag
              ><span v-if="!target.regions.length" class="field-help"
                >尚未选择地区</span
              >
            </div>
            <div class="region-tools">
              <el-button size="small" @click="openPicker(target.key)"
                >选择地区</el-button
              ><el-button
                size="small"
                :disabled="!remaining.length"
                @click="assignRemaining(target.key)"
                >加入全部剩余地区</el-button
              ><el-button
                v-if="target.index !== undefined"
                type="text"
                class="danger-link"
                @click="removeGroup(target.index)"
                >删除分组</el-button
              >
            </div>
          </el-card>
          <el-button class="add-fee-group" icon="el-icon-plus" @click="addGroup"
            >添加收费分组</el-button
          >
        </div>
        <aside class="editor-summary">
          <el-card shadow="never"
            ><h3>地区概览</h3>
            <div v-for="stat in stats" :key="stat.label" class="summary-row">
              <span>{{ stat.label }}</span
              ><strong>{{ stat.count }}</strong>
            </div>
            <el-progress :percentage="coverage" :show-text="false" />
            <p v-if="remaining.length" class="unassigned">
              还有 {{ remaining.length }} 个地区未分配<br />{{
                remaining
                  .slice(0, 5)
                  .map(c => c.name)
                  .join("、")
              }}{{ remaining.length > 5 ? "…" : "" }}
            </p>
            <p v-else class="configured">
              全部 {{ allUnits.length }} 个配送地区已配置
            </p>
            <p class="field-help">
              直辖市按全市、港澳台按整体配置。地区编码与商城收货地址库一致。
            </p>
          </el-card>
        </aside>
      </div>
      <footer class="freight-savebar">
        <span>{{ dirty ? "有未保存的修改" : "修改保存后生效" }}</span
        ><el-button :disabled="saving" @click="cancel">取消</el-button
        ><el-button type="primary" :loading="saving" @click="save"
          >保存模板</el-button
        >
      </footer>
      <freight-region-picker
        :visible="pickerVisible"
        :title="pickerTitle"
        :provinces="provinces"
        :value="selected"
        :occupied="occupied"
        @close="pickerVisible = false"
        @confirm="confirmPicker"
      />
    </template>
  </section>
</template>
<script>
import FreightRegionPicker from "./FreightRegionPicker.vue";
import { freightRegions, freightDetail, freightSave } from "@/api/freight";
import {
  units,
  compact,
  ownership,
  validateTemplate,
  feeCents
} from "@/utils/freight-regions";
const blank = () => ({
  id: null,
  name: "",
  revision: 0,
  updated: 0,
  free: [],
  groups: [],
  blocked: []
});
export default {
  components: { FreightRegionPicker },
  props: { templateId: { default: null } },
  data() {
    return {
      form: blank(),
      provinces: [],
      baseline: "",
      loading: false,
      ready: false,
      loadError: "",
      saving: false,
      errors: [],
      pickerVisible: false,
      pickerTarget: "free"
    };
  },
  computed: {
    dirty() {
      return this.ready && JSON.stringify(this.form) !== this.baseline;
    },
    nameLength() {
      return Array.from(this.form.name.trim()).length;
    },
    allUnits() {
      return units(this.provinces);
    },
    remaining() {
      const assigned = ownership(this.form);
      return this.allUnits.filter(c => !assigned[c.id]);
    },
    targets() {
      return [
        { key: "free", title: "包邮地区", regions: this.form.free },
        ...this.form.groups.map((g, i) => ({
          key: `g${i}`,
          index: i,
          title: `收费分组 ${i + 1}`,
          regions: g.regions
        })),
        { key: "blocked", title: "不配送地区", regions: this.form.blocked }
      ];
    },
    stats() {
      return [
        { label: "包邮地区", count: this.form.free.length },
        {
          label: "收费地区",
          count: this.form.groups.reduce((n, g) => n + g.regions.length, 0)
        },
        { label: "不配送地区", count: this.form.blocked.length },
        { label: "未分配地区", count: this.remaining.length }
      ];
    },
    coverage() {
      return this.allUnits.length
        ? Math.round((1 - this.remaining.length / this.allUnits.length) * 100)
        : 0;
    },
    pickerTitle() {
      const target = this.targets.find(t => t.key === this.pickerTarget);
      return `选择${target ? target.title : "地区"}`;
    },
    selected() {
      const target = this.targets.find(t => t.key === this.pickerTarget);
      return target ? target.regions : [];
    },
    occupied() {
      return ownership(this.form, this.pickerTarget);
    },
    nameError() {
      return this.errors.length &&
        (!this.form.name.trim() ||
          Array.from(this.form.name.trim()).length > 30)
        ? "名称必填，最多30字"
        : "";
    }
  },
  created() {
    this.load();
  },
  mounted() {
    window.addEventListener("beforeunload", this.unload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.unload);
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const [regionResponse, detailResponse] = await Promise.all([
          freightRegions(),
          this.templateId
            ? freightDetail(this.templateId)
            : Promise.resolve({ bizcode: 100, data: blank() })
        ]);
        if (regionResponse.bizcode !== 100 || detailResponse.bizcode !== 100)
          throw new Error(
            regionResponse.bizcode !== 100
              ? regionResponse.msg
              : detailResponse.msg
          );
        this.provinces = regionResponse.data;
        this.form = detailResponse.data;
        // 货币用十进制字符串编辑，不经Number四舍五入。
        this.form.groups.forEach(g => {
          g.fee = String(g.fee);
        });
        this.baseline = JSON.stringify(this.form);
        this.ready = true;
      } catch (e) {
        this.loadError = e.message || "加载运费模板失败，请重试";
      } finally {
        this.loading = false;
      }
    },
    tags(codes) {
      return compact(codes, this.provinces);
    },
    setRegions(key, codes) {
      if (key === "free" || key === "blocked") this.form[key] = codes;
      else this.form.groups[Number(key.slice(1))].regions = codes;
    },
    openPicker(key) {
      this.pickerTarget = key;
      this.pickerVisible = true;
    },
    confirmPicker(codes) {
      this.setRegions(this.pickerTarget, codes);
      this.pickerVisible = false;
    },
    assignRemaining(key) {
      const target = this.targets.find(t => t.key === key);
      this.setRegions(key, [
        ...target.regions,
        ...this.remaining.map(c => c.id)
      ]);
    },
    remove(key, ids) {
      const target = this.targets.find(t => t.key === key);
      this.setRegions(
        key,
        target.regions.filter(id => !ids.includes(id))
      );
    },
    addGroup() {
      this.form.groups.push({ fee: "", regions: [] });
    },
    async removeGroup(index) {
      if (this.form.groups[index].regions.length) {
        try {
          await this.$confirm(
            "删除后，该组地区会恢复为未分配，保存前需要重新分配。",
            "删除收费分组",
            { type: "warning" }
          );
        } catch (_) {
          return;
        }
      }
      this.form.groups.splice(index, 1);
    },
    feeError(index) {
      return this.errors.length &&
        feeCents(this.form.groups[index].fee) === null
        ? "请输入合法固定运费"
        : "";
    },
    showErrors(errors) {
      this.errors = errors;
      this.$nextTick(() => {
        if (this.$refs.errors) this.$refs.errors.focus();
      });
    },
    async save() {
      if (this.saving || !this.ready) return;
      const errors = validateTemplate(this.form, this.provinces);
      if (errors.length) return this.showErrors(errors);
      this.saving = true;
      this.errors = [];
      try {
        const response = await freightSave({
          ...this.form,
          name: this.form.name.trim()
        });
        if (response.bizcode !== 100)
          return this.showErrors([response.msg || "保存失败，请检查后重试"]);
        this.form = response.data;
        this.baseline = JSON.stringify(this.form);
        this.$message.success("运费模板已保存");
        this.$emit("saved", response.data);
      } catch (e) {
        this.showErrors([e.message || "保存失败，填写内容已保留，请重试"]);
      } finally {
        this.saving = false;
      }
    },
    async confirmLeave() {
      if (this.saving) return false;
      if (!this.dirty) return true;
      try {
        await this.$confirm(
          "当前修改尚未保存，离开后将丢失。",
          "放弃未保存的修改？",
          {
            type: "warning",
            confirmButtonText: "放弃修改",
            cancelButtonText: "继续编辑"
          }
        );
        return true;
      } catch (_) {
        return false;
      }
    },
    async cancel() {
      if (await this.confirmLeave()) this.$emit("cancel");
    },
    unload(event) {
      if (this.dirty || this.saving) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
  }
};
</script>
<style scoped>
.freight-editor {
  min-height: 240px;
}
.editor-title h2 {
  margin: 0 0 10px;
}
.editor-title p,
.field-help {
  color: #606266;
  font-size: 13px;
  line-height: 1.65;
}
.editor-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: 20px;
  margin-top: 20px;
}
.editor-main {
  min-width: 0;
}
.editor-main > .el-card {
  margin-bottom: 18px;
}
.freight-editor h3 {
  font-size: 16px;
  margin: 0 0 18px;
}
.rule-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rule-heading h3 {
  margin: 0;
}
.rule-heading span {
  font-size: 13px;
  color: #606266;
}
.rule-card.free {
  border-top: 3px solid #46a889;
}
.rule-card.blocked {
  border-top: 3px solid #e87878;
}
.region-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 180px;
  overflow: auto;
  min-height: 34px;
}
.region-tags .el-tag {
  height: auto;
  white-space: normal;
}
.region-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #ebeef5;
  margin-top: 14px;
  padding-top: 14px;
  flex-wrap: wrap;
}
.region-tools > .el-button {
  margin-left: 0;
}
.fee-input {
  max-width: 230px;
  margin-right: 12px;
}
.editor-summary {
  align-self: start;
  position: sticky;
  top: 16px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  margin: 18px 0;
  font-size: 14px;
}
.unassigned {
  font-size: 13px;
  line-height: 1.7;
  color: #a66b12;
  background: #fdf6ec;
  padding: 12px;
}
.configured {
  font-size: 13px;
  color: #278567;
}
.freight-savebar {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #ebeef5;
  padding: 16px 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  z-index: 2;
}
.freight-savebar > span {
  margin-right: auto;
  font-size: 13px;
  color: #606266;
}
.freight-savebar > .el-button {
  margin-left: 0;
}
.freight-errors {
  padding: 16px;
  background: #fef0f0;
  color: #b53c3c;
  line-height: 1.7;
  margin: 16px 0;
  border-radius: 4px;
}
.freight-errors ul {
  margin-bottom: 0;
}
.danger-link {
  color: #d45353;
}
.add-fee-group {
  width: 100%;
  margin-bottom: 20px;
}
@media (max-width: 1000px) {
  .editor-columns {
    grid-template-columns: minmax(0, 1fr);
  }
  .editor-summary {
    position: static;
  }
  .summary-row {
    display: inline-flex;
    gap: 16px;
    margin: 8px 18px 12px 0;
  }
}
</style>
